import { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { getCashOutRule, updateCashOutRule } from "services/cashOutRule";

const CashOutRuleForm = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rule, setRule] = useState({ minAmount: "", maxAmount: "", cutoffDays: "" });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getCashOutRule();
        if (data) {
          setRule({
            minAmount: data.minAmount?.toString() ?? "",
            maxAmount: data.maxAmount?.toString() ?? "",
            cutoffDays: data.cutoffDays?.toString() ?? "",
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const validate = () => {
    const e = {};
    const min = parseFloat(rule.minAmount || 0);
    const max = parseFloat(rule.maxAmount || 0);
    const days = parseInt(rule.cutoffDays || 0, 10);

    if (isNaN(min) || min < 0) e.minAmount = "Min amount must be a non-negative number.";
    if (isNaN(max) || max < 0) e.maxAmount = "Max amount must be a non-negative number.";
    if (!e.minAmount && !e.maxAmount && min > max) e.maxAmount = "Max amount must be >= Min amount.";
    if (isNaN(days) || days < 0) e.cutoffDays = "Cutoff days must be a non-negative integer.";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        minAmount: parseFloat(rule.minAmount || 0),
        maxAmount: parseFloat(rule.maxAmount || 0),
        cutoffDays: parseInt(rule.cutoffDays || 0, 10),
      };
      await updateCashOutRule(payload);
      // refresh
      const updated = await getCashOutRule();
      if (updated) {
        setRule({
          minAmount: updated.minAmount?.toString() ?? "",
          maxAmount: updated.maxAmount?.toString() ?? "",
          cutoffDays: updated.cutoffDays?.toString() ?? "",
        });
      }
      setSnackbar({ open: true, severity: "success", message: "Cash Out Rule saved" });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, severity: "error", message: "Failed to save. Try again." });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", p: 2 }}>
      <Card sx={{ width: "100%", maxWidth: 920, borderRadius: 2, boxShadow: 3 }}>
        <CardHeader
          title={<Typography variant="h6">Wallets — Cash Out Rule</Typography>}
          subheader={<Typography variant="body2" color="text.secondary">Set global cash-out constraints used across requests</Typography>}
          sx={{ pb: 0 }}
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Min Amount"
                  name="minAmount"
                  value={rule.minAmount}
                  onChange={(e) => setRule((r) => ({ ...r, minAmount: e.target.value }))}
                  error={!!errors.minAmount}
                  helperText={errors.minAmount || "Minimum allowed cash-out amount"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">JOD</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Amount"
                  name="maxAmount"
                  value={rule.maxAmount}
                  onChange={(e) => setRule((r) => ({ ...r, maxAmount: e.target.value }))}
                  error={!!errors.maxAmount}
                  helperText={errors.maxAmount || "Maximum allowed cash-out amount"}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">JOD</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cutoff Days"
                  name="cutoffDays"
                  value={rule.cutoffDays}
                  onChange={(e) => setRule((r) => ({ ...r, cutoffDays: e.target.value }))}
                  error={!!errors.cutoffDays}
                  helperText={errors.cutoffDays || "Number of days before payout is allowed"}
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                  </Button>

                  <Button
                    variant="outlined"
                    disabled={loading || saving}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const data = await getCashOutRule();
                        if (data)
                          setRule({
                            minAmount: data.minAmount?.toString() ?? "",
                            maxAmount: data.maxAmount?.toString() ?? "",
                            cutoffDays: data.cutoffDays?.toString() ?? "",
                          });
                      } catch (e) {
                        console.error(e);
                        setSnackbar({ open: true, severity: "error", message: "Failed to reload" });
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    Reload
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashOutRuleForm;
